// Section-heading detection and splicing for "regenerate this section"
// (C6, docs/HMS_V4_AI_CONTEXT_BUILDING_ASSESSMENT.md §5.2). Documents in this
// app are line-oriented (see documentModel.ts): one markdown line is one
// paragraph, always, in the same order — so a heading's line index doubles
// as its section's identity across both the editor and a fresh AI reply.
//
// The three heading shapes recognised here are the same "bare label" rule
// backend/utils/docxGenerator.js and backend/utils/aiEditObservation.js's
// extractSections() already use — this is an independent frontend port of
// that same idea, not a shared import (frontend/backend are separate
// bundles, same reasoning as those two backend files already being
// independent ports of each other).

export interface DocSection {
  heading: string;
  key: string;
  /** First line of the section (the heading line itself). */
  startLine: number;
  /** One past the section's last line (the next heading's startLine, or the document's line count). */
  endLine: number;
}

export function isHeadingLine(line: string): { heading: string; key: string } | null {
  const trimmed = line.trim();
  if (!trimmed) return null;

  let heading: string | null = null;
  const markdown = trimmed.match(/^#{1,6}\s+(.+)$/);
  const wrapped = trimmed.match(/^(\*{1,2}|__)(.+?)\1:?$/);
  if (markdown) heading = markdown[1] ?? null;
  else if (wrapped) heading = wrapped[2] ?? null;
  else if (/^[A-Za-z][^:]{0,40}:$/.test(trimmed)) heading = trimmed.slice(0, -1);

  if (!heading) return null;
  const key = heading.replace(/[*_#:`]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
  if (!key || key.length > 40) return null;
  return { heading: heading.trim(), key };
}

export function detectSections(markdown: string): DocSection[] {
  const lines = String(markdown ?? '').replace(/\r\n?/g, '\n').split('\n');
  const sections: DocSection[] = [];
  lines.forEach((line, i) => {
    const match = isHeadingLine(line);
    if (!match) return;
    const previous = sections[sections.length - 1];
    if (previous) previous.endLine = i;
    sections.push({ heading: match.heading, key: match.key, startLine: i, endLine: lines.length });
  });
  return sections;
}

/** Replaces `section`'s line range in `markdown` with `replacementLines`; every other line is untouched. */
export function replaceSection(markdown: string, section: DocSection, replacementLines: string[]): string {
  const lines = String(markdown ?? '').replace(/\r\n?/g, '\n').split('\n');
  lines.splice(section.startLine, section.endLine - section.startLine, ...replacementLines);
  return lines.join('\n');
}

/**
 * Finds `key`'s section in both `current` and `reply`, and splices the
 * reply's version of just that section into `current`. Returns null when the
 * reply doesn't contain a matching heading (the model renamed or dropped
 * it) — the caller should show an error rather than silently accepting a
 * full-document replace, since that would defeat the point of "regenerate
 * only this section."
 */
export function spliceSectionFromReply(current: string, reply: string, key: string): string | null {
  const currentSection = detectSections(current).find((s) => s.key === key);
  const replySection = detectSections(reply).find((s) => s.key === key);
  if (!currentSection || !replySection) return null;

  const replyLines = String(reply ?? '').replace(/\r\n?/g, '\n').split('\n');
  const replacementLines = replyLines.slice(replySection.startLine, replySection.endLine);
  return replaceSection(current, currentSection, replacementLines);
}

/** A section's own lines, heading included. */
export function sectionBody(markdown: string, section: DocSection): string[] {
  const lines = String(markdown ?? '').replace(/\r\n?/g, '\n').split('\n');
  return lines.slice(section.startLine, section.endLine);
}

export interface SectionChange {
  key: string;
  heading: string;
  kind: 'added' | 'changed' | 'removed';
}

/**
 * Section-level diff between two full-document strings, matched by heading
 * `key` (stable across a revision — see this file's own header comment).
 * Used by the v3 conversational layout's change ribbon and per-turn
 * transcript chips (docs/design/consultation/Conversation.dc.html marker 2)
 * — client-only, ephemeral: nothing here is persisted or sent to the model,
 * it just narrates a diff the doctor can already see in the editor.
 */
export function diffSections(oldDoc: string, newDoc: string): SectionChange[] {
  const oldSections = detectSections(oldDoc);
  const newSections = detectSections(newDoc);
  const oldByKey = new Map(oldSections.map((s) => [s.key, s]));
  const newByKey = new Map(newSections.map((s) => [s.key, s]));
  const changes: SectionChange[] = [];

  for (const section of newSections) {
    const previous = oldByKey.get(section.key);
    if (!previous) {
      changes.push({ key: section.key, heading: section.heading, kind: 'added' });
      continue;
    }
    const oldBody = sectionBody(oldDoc, previous).join('\n');
    const newBody = sectionBody(newDoc, section).join('\n');
    if (oldBody !== newBody) changes.push({ key: section.key, heading: section.heading, kind: 'changed' });
  }
  for (const section of oldSections) {
    if (!newByKey.has(section.key)) changes.push({ key: section.key, heading: section.heading, kind: 'removed' });
  }
  return changes;
}
